/**
 * DesktopLayout Component
 * 
 * Modal layout for desktop editing.
 * Features:
 * - Centered modal with backdrop
 * - Save/Cancel buttons at top
 * - Full form fields
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { EditableItem } from '../types';
import { ItemForm } from '../ItemForm';

export interface DesktopLayoutProps {
  item: EditableItem;
  onChange: (patch: Partial<EditableItem>) => void;
  onSave: () => Promise<void>;
  onClose: () => void;
  token?: string;
  readOnly?: boolean;
  saveDisabled?: boolean;
  saving?: boolean;
}

const btnStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  border: 'none',
  transition: 'all 0.15s',
};

export const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  item,
  onChange,
  onSave,
  onClose,
  token,
  readOnly = false,
  saveDisabled = false,
  saving = false,
}) => {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!item.name.trim()) {
      alert('Item name is required');
      return;
    }

    if (!saveDisabled && !readOnly) {
      await onSave();
    }
  };

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-text-inverse)',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* Item ID at very top */}
          {item.id && (
            <div style={{
              textAlign: 'center',
              padding: '12px 24px',
              borderBottom: '1px solid var(--color-border)',
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
            }}>
              #{item.id}
            </div>
          )}
          
          {/* Header with Save/Cancel buttons */}
          <div
            style={{
              padding: '16px 24px',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                ...btnStyle,
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                opacity: saving ? 0.6 : 1,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            {!readOnly && (
              <button
                type="submit"
                disabled={saving || saveDisabled || !item.name.trim()}
                style={{
                  ...btnStyle,
                  background: 'var(--color-accent-secondary)',
                  color: 'var(--color-text-inverse)',
                  opacity: saving || saveDisabled || !item.name.trim() ? 0.6 : 1,
                  cursor: saving || saveDisabled || !item.name.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>

          {/* Scrollable Body */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
            {/* Form */}
            <ItemForm
              item={item}
              onChange={onChange}
              token={token}
              readOnly={readOnly}
              mode="full"
            />

            {/* Timestamps at bottom - on same line with icons */}
            {(item.created_at || item.updated_at || item.purchased_at) && (
              <div
                style={{
                  marginTop: '24px',
                  paddingTop: '16px',
                  borderTop: '1px solid var(--color-border)',
                  fontSize: '13px',
                  color: 'var(--color-text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                {item.created_at && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="16"></line>
                      <line x1="8" y1="12" x2="16" y2="12"></line>
                    </svg>
                    {new Date(item.created_at).toLocaleString(undefined, {
                      month: 'numeric',
                      day: 'numeric',
                      year: '2-digit',
                    })}
                  </span>
                )}
                {item.created_at && (item.updated_at || item.purchased_at) && <span>•</span>}
                {item.updated_at && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                    </svg>
                    {new Date(item.updated_at).toLocaleString(undefined, {
                      month: 'numeric',
                      day: 'numeric',
                      year: '2-digit',
                    })}
                  </span>
                )}
                {item.updated_at && item.purchased_at && <span>•</span>}
                {item.purchased_at && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="9" cy="21" r="1"></circle>
                      <circle cx="20" cy="21" r="1"></circle>
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                    </svg>
                    {new Date(item.purchased_at).toLocaleString(undefined, {
                      month: 'numeric',
                      day: 'numeric',
                      year: '2-digit',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </span>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default DesktopLayout;
